import { DurationRange, ContentRequirement, ContentType, ContentSubmissionUnit, UgcRateRange, Campaign, Collab, isVoided } from '@foodfluence/shared';

/**
 * Resolved content requirements for a collab: prefer snapshot on the collab, else campaign template.
 */
export const getCollabContentRequirements = (
  collab: Collab | undefined,
  campaign: Campaign | undefined
): Record<string, ContentRequirement> | undefined => {
  const fromCollab = collab?.contentRequirements;
  if (fromCollab && Object.keys(fromCollab).length > 0) {
    return fromCollab;
  }
  if (campaign?.contentRequirements && Object.keys(campaign.contentRequirements).length > 0) {
    return campaign.contentRequirements;
  }
  // Pre-acceptance custom proposals: only the creator's offer exists
  const fromProposed = collab?.proposedContent;
  if (fromProposed && Object.keys(fromProposed).length > 0) {
    return fromProposed;
  }
  return undefined;
};

/**
 * The creator's own UGC ask while their request or counter is pending — the UGC mirror of
 * `proposedInfluencerDeliverableCounts` (see formatDeliverableString). Undefined otherwise: then
 * the brand's offer / agreed spec applies (getCollabContentRequirements).
 */
export const getPendingUgcAsk = (
  collab: Collab | undefined
): Record<string, ContentRequirement> | undefined => {
  const isPendingAsk = collab?.status === 'pendingClientApproval' || collab?.status === 'influencerCountered';
  return isPendingAsk && Object.keys(collab?.proposedContent ?? {}).length > 0 ? collab?.proposedContent : undefined;
};

/**
 * Gets content type display name based on duration
 */
export const getContentTypeDisplayName = (duration?: DurationRange, type?: ContentType): string => {
  if (type === 'photo') {
    return 'Photo';
  }
  if (!duration) return 'Content';

  if (duration.unit === 'seconds') {
    if (duration.min && duration.max) return `${duration.min}-${duration.max} Second Video`;
    if (duration.min) return `${duration.min}+ Second Video`;
    if (duration.max) return `Up to ${duration.max} Second Video`;
    return 'Video Content';
  } else if (duration.unit === 'minutes') {
    if (duration.min && duration.max) return `${duration.min}-${duration.max} Minute Video`;
    if (duration.min) return `${duration.min}+ Minute Video`;
    if (duration.max) return `Up to ${duration.max} Minute Video`;
    return 'Video Content';
  }

  return 'Content';
};

/**
 * Gets formatted duration text
 */
export const getContentDurationText = (duration?: DurationRange): string => {
  if (!duration) return '';
  
  // If there's a displayText, use that
  if (duration.displayText) return duration.displayText;

  // Otherwise construct from min/max
  if (duration.min && duration.max) {
    return `${duration.min}-${duration.max} ${duration.unit}`;
  } else if (duration.min) {
    return `${duration.min}+ ${duration.unit}`;
  } else if (duration.max) {
    return `Up to ${duration.max} ${duration.unit}`;
  }

  return `${duration.unit}`;
};

/**
 * Gets content type count (usually 1 unless specified)
 */
export const getContentTypeCount = (requirement: ContentRequirement): number => {
  return requirement?.count || 1;
};

export type ContentPreset = 'short' | 'standard' | 'long' | 'extended' | 'photo';

export const getContentPreset = (duration?: { min?: number; max?: number; unit?: string }, type?: string): ContentPreset => {
    // Check for photo type first
  if (type === 'photo') return 'photo';

  if (!duration || !duration.unit) return 'short';

  const { min, max, unit } = duration;
  if (unit === 'seconds') {
    if (!min && max === 30) return 'short';
    if (min === 30 && max === 60) return 'standard';
    if (min === 60 && !max) return 'long';
    if (min === 15) return 'extended';
  }
  return 'short'; // default
};

export const getContentPresetDisplayName = (preset: ContentPreset): string => {
  console.log(preset);
  switch (preset) {
    case 'short':
      return 'Short Form Video';
    case 'standard':
      return 'Standard Video';
    case 'long':
      return 'Long Form Video';
    case 'extended':
      return 'Extended Video';
    case 'photo':
      return 'Photo';
    default:
      return '';
  }
};

/**
 * Checks if a content requirement has any guidelines (brief file or structured guidelines)
 */
export const hasContentGuidelines = (requirement: ContentRequirement): boolean => {
  const { guidelines } = requirement;
  return !!(
    requirement.contentBriefFile ||
    guidelines?.dos?.length ||
    guidelines?.donts?.length ||
    guidelines?.mustInclude?.length ||
    guidelines?.additionalInfo
  );
};

/**
 * Checks if a content requirement is a video type
 */
export const isVideoRequirement = (requirement: ContentRequirement): boolean => {
  return requirement.type === 'video';
};

/**
 * Checks if a content requirement is for a carousel (multiple items)
 */
export const isCarouselRequirement = (requirement: ContentRequirement): boolean => {
  return (requirement?.count || 1) > 1;
};

export interface UgcRatePreset {
  duration: DurationRange | null;
  type: ContentType;
  count?: number;
  displayName: string;
}

export const UGC_RATE_PRESETS: UgcRatePreset[] = [
  { duration: { max: 30, unit: 'seconds' }, type: 'video', displayName: 'Short Form (0–30s)' },
  { duration: { min: 30, max: 60, unit: 'seconds' }, type: 'video', displayName: 'Medium Form (30–60s)' },
  { duration: { min: 60, unit: 'seconds' }, type: 'video', displayName: 'Long Form (60s+)' },
  { duration: { min: 15, unit: 'seconds' }, type: 'video', displayName: 'Story (15s vertical)' },
  { duration: null, type: 'photo', count: 1, displayName: 'Single Photo' },
  { duration: null, type: 'photo', count: 2, displayName: 'Multiple Photos (2+)' },
];

export const ugcDeliverableMatch = (rate: Pick<UgcRateRange, 'type' | 'duration' | 'count'>): UgcRatePreset | undefined => {
  return UGC_RATE_PRESETS.find((p) => {
    const a = p.duration;
    const b = rate.duration ?? null;
    const durationsMatch = a === null && b === null ? true : a === null || b === null ? false : a.min === b.min && a.max === b.max && a.unit === b.unit;
    let countMatches = true

    if (rate.count) {
      countMatches = p.count === 2 && rate.type === 'photo' && (rate.count ?? 1) >= 2
        ? true
        : p.count === rate.count;
    }
    return p.type === rate.type && durationsMatch && countMatches;
  });
};

export const getUgcRateDisplayName = (rate: Pick<UgcRateRange, 'type' | 'duration' | 'count'>): string => {
  const match = ugcDeliverableMatch(rate);
  if (match) return match.displayName;
  if (rate.type === 'photo') return rate.count && rate.count > 1 ? `Photo (${rate.count})` : 'Photo';
  return getContentTypeDisplayName(rate.duration ?? undefined, rate.type);
};

/**
 * Normalizes campaign contentRequirements into matched UgcRatePresets.
 * Each ContentRequirement is mapped to a UgcRateRange and matched via ugcDeliverableMatch.
 * Returns only entries with a recognized preset.
 */
export const getContentRequirementsAsPresets = (
  contentRequirements: Record<string, ContentRequirement> | undefined
): Array<{ preset: UgcRatePreset; requirement: ContentRequirement }> => {
  if (!contentRequirements) return [];

  return Object.values(contentRequirements)
    .map((requirement) => {
      const rateRange: Pick<UgcRateRange, 'type' | 'duration' | 'count'> = {
        type: requirement.type,
        duration: requirement.duration ?? null,
        count: requirement.count,
      };
      const preset = ugcDeliverableMatch(rateRange);
      return preset ? { preset, requirement } : null;
    })
    .filter((item): item is { preset: UgcRatePreset; requirement: ContentRequirement } => item !== null);
};


export interface UgcDeliverableItem {
  requirement: ContentRequirement;
  type: ContentType;
  /** 1-based position among the items of the same type */
  index: number;
  /** How many items of this type are owed */
  ofType: number;
  /** Also posted on the creator's socials: the requirement (or its unit) names channels */
  posted: boolean;
}

export interface UgcDeliverablesSummary {
  /** One entry per piece of content owed: videos first, then photos */
  items: UgcDeliverableItem[];
  videos: number;
  photos: number;
  /** Duration text every video shares (e.g. "Up to 30 sec"); null when they differ, have none, or there are no videos */
  sharedVideoDuration: string | null;
  /** The collab still carries influencer-style post deliverables (deliverableCounts) next to the UGC */
  owesSocialPosts: boolean;
}

/**
 * Every UGC piece the creator owes, expanded to one item each — the dashboard stores N videos as N
 * separate requirements and N photos as one requirement with `count: N`, so neither shape tells the
 * creator a total on its own.
 *
 * Each requirement counts at the units still owed, not at `requirement.count`: the count records what
 * the brand originally asked for and is not decremented when a unit is voided. A requirement with no
 * units yet keeps its declared count — units are generated at acceptance (keyed by requirement key),
 * and a brief read before that would otherwise show nothing.
 *
 * Deliberately not filtered through UGC_RATE_PRESETS: a requirement whose duration matches no preset
 * still has to be shown.
 */
export const summarizeUgcDeliverables = (
  collab: Collab | undefined,
  campaign: Campaign | undefined
): UgcDeliverablesSummary => {
  const requirements = Object.entries(getPendingUgcAsk(collab) ?? getCollabContentRequirements(collab, campaign) ?? {});
  const units = Object.values(collab?.content ?? {});
  const liveUnitCounts = units.reduce<Record<string, number>>((acc, unit) => {
    if (!isVoided(unit)) acc[unit.requirementId] = (acc[unit.requirementId] ?? 0) + 1;
    return acc;
  }, {});
  const hasUnitsFor = new Set(units.map(unit => unit.requirementId));
  const owed = (key: string, requirement: ContentRequirement) =>
    Math.max(0, hasUnitsFor.has(key) ? liveUnitCounts[key] ?? 0 : requirement.count ?? 1);
  // Same rule as UgcSubmissionPage's `ugcHasChannels`: channels on the requirement or its units mean a post-link step.
  const postedUnitsFor = new Set(units.filter(unit => !isVoided(unit) && unit.channels?.length).map(unit => unit.requirementId));
  const isPosted = (key: string, requirement: ContentRequirement) => !!requirement.channels?.length || postedUnitsFor.has(key);

  const itemsOfType = (type: ContentType): UgcDeliverableItem[] => {
    const owedOfType = requirements
      .filter(([, requirement]) => requirement.type === type)
      .flatMap(([key, requirement]) =>
        Array.from({ length: owed(key, requirement) }, () => ({ requirement, posted: isPosted(key, requirement) }))
      );
    return owedOfType.map((owedItem, i) => ({ ...owedItem, type, index: i + 1, ofType: owedOfType.length }));
  };

  const videoItems = itemsOfType('video');
  const photoItems = itemsOfType('photo');
  const durations = new Set(videoItems.map(item => getShortDurationText(item.requirement.duration)));
  const [onlyDuration] = Array.from(durations);

  return {
    items: [...videoItems, ...photoItems],
    videos: videoItems.length,
    photos: photoItems.length,
    sharedVideoDuration: durations.size === 1 && onlyDuration ? onlyDuration : null,
    owesSocialPosts: Object.values(collab?.deliverableCounts ?? {}).some(count => count > 0),
  };
};

export const pluralize = (n: number, noun: string) => `${n} ${noun}${n === 1 ? '' : 's'}`;

/** "Up to 30 sec" → "up to 30 sec", for use mid-sentence. */
export const lowerFirst = (text: string) => text.charAt(0).toLowerCase() + text.slice(1);

/** "6 unique UGC videos" / "1 UGC video" — "unique" because each piece must be different content, not copies. */
const countUgc = (n: number, noun: string) => (n > 1 ? `${n} unique UGC ${noun}s` : `${n} UGC ${noun}`);

/**
 * One-line deliverables summary shown above the Accept button, e.g. "6 unique UGC videos · up to 30 sec each".
 * Null when nothing is owed.
 */
export const formatUgcDeliverablesLine = ({ videos, photos, sharedVideoDuration }: UgcDeliverablesSummary): string | null => {
  if (videos && photos) return `${countUgc(videos, 'video')} + ${pluralize(photos, 'photo')}`;
  if (photos) return countUgc(photos, 'photo');
  if (!videos) return null;
  if (!sharedVideoDuration) return countUgc(videos, 'video');
  return `${countUgc(videos, 'video')} · ${lowerFirst(sharedVideoDuration)}${videos > 1 ? ' each' : ''}`;
};

/**
 * Whether the UGC also gets posted on the creator's own socials. A UGC requirement with `channels` is a
 * posting ask (the dashboard labels it "where the creator will post this content"); without channels the
 * content is only uploaded. Null — no claim either way — when the collab still owes influencer-style posts,
 * which existing collabs keep when a campaign is switched from influencer to UGC.
 */
export const formatUgcPostingNote = ({ items, owesSocialPosts }: UgcDeliverablesSummary): string | null => {
  if (items.length === 0 || owesSocialPosts) return null;
  const posted = items.filter(item => item.posted).length;
  if (posted === 0) return 'No posting to your socials required. Just upload in FoodFluence.';
  if (posted === items.length) {
    return items.length === 1 ? "You'll also post it to your socials." : "You'll also post each one to your socials.";
  }
  return `${posted} of ${items.length} also ${posted === 1 ? 'needs' : 'need'} posting to your socials.`;
};

/**
 * Counts content requirements per matching UGC preset displayName.
 * Inverse of applyPresetCountsToRequirements; seeds the proposal picker in update mode.
 */
export const getUgcPresetCounts = (
  contentRequirements?: Record<string, ContentRequirement>
): Record<string, number> => {
  return getContentRequirementsAsPresets(contentRequirements).reduce<Record<string, number>>(
    (counts, { preset }) => {
      counts[preset.displayName] = (counts[preset.displayName] || 0) + 1;
      return counts;
    },
    {}
  );
};

/**
 * Applies preset-level count targets to an existing requirements map, preserving the
 * original requirement objects (guidelines, channels, briefs) wherever possible.
 * Requirements that match no preset are kept untouched rather than silently dropped.
 */
export const applyPresetCountsToRequirements = (
  current: Record<string, ContentRequirement>,
  targetCounts: Record<string, number>
): Record<string, ContentRequirement> => {
  const next: Record<string, ContentRequirement> = {};
  const byPreset: Record<string, Array<[string, ContentRequirement]>> = {};

  Object.entries(current).forEach(([key, requirement]) => {
    const preset = ugcDeliverableMatch({
      type: requirement.type,
      duration: requirement.duration ?? null,
      count: requirement.count,
    });
    if (preset) {
      if (!byPreset[preset.displayName]) byPreset[preset.displayName] = [];
      byPreset[preset.displayName].push([key, requirement]);
    } else {
      next[key] = requirement;
    }
  });

  UGC_RATE_PRESETS.forEach((preset) => {
    const existing = byPreset[preset.displayName] ?? [];
    const target = targetCounts[preset.displayName] ?? existing.length;
    existing.slice(0, target).forEach(([key, requirement]) => {
      next[key] = requirement;
    });
    for (let i = existing.length; i < target; i++) {
      const id = crypto.randomUUID();
      const addition: ContentRequirement = { id, type: preset.type };
      if (preset.type === 'video' && preset.duration) {
        addition.duration = preset.duration;
      }
      if (preset.type === 'photo' && preset.count) {
        addition.count = preset.count;
      }
      next[id] = addition;
    }
  });

  return next;
};

/**
 * Short human-readable duration text for display in compact cards (e.g. "15–30 sec").
 */
export const getShortDurationText = (duration?: DurationRange): string => {
  if (!duration) return '';
  if (duration.displayText) return duration.displayText;
  const unitShort = duration.unit === 'seconds' ? 'sec' : duration.unit === 'minutes' ? 'min' : 'hr';
  if (duration.min && duration.max) return `${duration.min}–${duration.max} ${unitShort}`;
  if (duration.min) return `${duration.min}+ ${unitShort}`;
  if (duration.max) return `Up to ${duration.max} ${unitShort}`;
  return unitShort;
};

/**
 * Checks if any of the content being submitted is new (status is pendingSubmission)
 */
export const hasNewContent = (
  contentMap: Record<string, ContentSubmissionUnit> | undefined,
  contentKeys: string[]
): boolean => {
  if (!contentMap) return true; // If no content map, consider all content new
  
  return contentKeys.some(contentKey => {
    const contentUnit = contentMap[contentKey];
    if (!contentUnit) return true; // New content if unit doesn't exist
    // A voided unit is not awaiting anything, so it must not read as new work.
    if (isVoided(contentUnit)) return false;

    // Check if content status is pendingSubmission (not yet submitted)
    return contentUnit.status === 'pendingSubmission';
  });
};
